import { Component, OnInit, ViewChild, ChangeDetectionStrategy } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator } from '@angular/material/paginator';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Permission } from '@core/enums/permissions';
import { SelectType } from '@core/enums/select';
import { StudentDemographic } from '@core/enums/student-demographic-form';
import { IModalEmitData } from '@core/interfaces/imodal';
import { IStateConfiguration } from '@core/interfaces/istate-configurations';
import {
  IStudentSearchRequestDto,
  IStudentSearchResponseDto,
} from '@core/interfaces/istudent';
import { AuthService } from '@core/services/auth.service';
import { PermissionService } from '@core/services/permission.service';
import { StateService } from '@core/services/state.service';
import { StudentService } from '@core/services/student.service';
import { ToastService } from '@core/services/toast.service';
import { LookupService } from '@core/services/lookup.service';
import { Observable, map, of, switchMap, take, tap } from 'rxjs';
import { TransferChildComponent } from '../transfer-child/transfer-child.component';
import { DropChildComponent } from '../drop-child/drop-child.component';
import { BatchReEnrollChildComponent } from '../batch-re-enroll-child/batch-re-enroll-child.component';
import { Modal } from '@core/enums/modal';
import { ConfirmationModalComponent } from '@shared/components/confirmation-modal/confirmation-modal.component';
import { ITableColumn } from '@core/interfaces/itable';
import { setIconDisplayObject } from '@core/services/helper.service';
import { StudentClassEnrollmentService } from '@core/services/student-class-enrollment.service';
import { RatingService } from '@core/services/rating.service';
import { ISites } from '@core/interfaces/isites';
import { IClassroom } from '@core/interfaces/iclasses';
import { TranslocoService } from '@jsverse/transloco';

@Component({
    selector: 'drdp-search-manage-child',
    templateUrl: './search-manage-child.component.html',
    styleUrls: ['./search-manage-child.component.scss'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class SearchManageChildComponent implements OnInit {
  @ViewChild(MatPaginator) paginator!: MatPaginator;
  childSearch: FormGroup | any;
  user = this.authService.getCurrentUser();
  isSuperUser: boolean = this.authService.isSuperUser();
  isSiteAdmin: boolean = this.authService.isSiteAdmin();
  isTeacher: boolean = this.authService.isTeacher();
  clearDropdowns = false;

  dataSource: MatTableDataSource<any> = new MatTableDataSource();
  pageSizeOptions: number[] = [10, 25, 50, 100];
  pageIndex: number = 0;
  pageSize: number = 10;
  totalData = 0;
  tableData?: any;
  ssidLabel = '';
  ssidPlaceholder = '';
  isSelectAll = false;
  studentBatch: IStudentSearchResponseDto[] = [];
  selectAllPagesSet: Set<number> = new Set<number>();
  initialAgencySetupComplete: boolean = false;
  initialSiteSetupComplete: boolean = false;
  isSoftImplementation = {
    agency: false,
    site: false,
    classroom: false
  }

  selectedSchoolYearId: number | null = null;
  currentSchoolYear: number = 0;
  private previousAgencyId: number | null = null;
  private previousRatingPeriodId: number | null = null;

  get firstName() {
    return this.childSearch.get('firstName');
  }
  get lastName() {
    return this.childSearch.get('lastName');
  }
  get stateId() {
    return this.childSearch.get('stateId');
  }
  get agencyId() {
    return this.childSearch.get('agencyId');
  }
  get SSID() {
    return this.childSearch.get('SSID');
  }
  get localId() {
    return this.childSearch.get('localId');
  }
  get ageGradeInstrumentId() {
    return this.childSearch.get('ageGradeInstrumentId');
  }
  get DOB() {
    return this.childSearch.get('DOB');
  }
  get drdpId() {
    return this.childSearch.get('drdpId');
  }
  get ratingPeriodId() {
    return this.childSearch.get('ratingPeriodId');
  }
  get classroomIds() {
    return this.childSearch.get('classroomIds');
  }
  get siteIds() {
    return this.childSearch.get('siteIds');
  }

  tableColumns: ITableColumn[] = [];

  public get selectType() {
    return SelectType;
  }
  constructor(
    private fb: FormBuilder,
    private authService: AuthService,
    private studentService: StudentService,
    private toastService: ToastService,
    private permissionService: PermissionService,
    private stateService: StateService,
    private router: Router,
    public modal: MatDialog,
    private enrollmentService: StudentClassEnrollmentService,
    private ratingService: RatingService,
    private lookupService: LookupService,
    private translocoService: TranslocoService
  ) {}

  ngOnInit(): void {
    this.buildTableColumns();

    this.ssidLabel = this.translocoService.translate(
      'user.manageEnrollment.searchManageChild.ssidLabel'
    );
    this.ssidPlaceholder = this.ssidLabel;

    this.getSsidLabel(this.user.stateId);
    this.initializeForm();
    this.lookupService
      .getCurrentSchoolYear()
      .pipe(take(1))
      .subscribe((year) => {
        this.currentSchoolYear = year.id;
        this.selectedSchoolYearId = year.id;
      });
  }

  buildTableColumns(): void {
    const t = (key: string) =>
      this.translocoService.translate(`user.manageEnrollment.searchManageChild.columns.${key}`);

    this.tableColumns = [
      {
        columnDef: 'isSelected',
        header: t('select'),
        type: 'checkBox',
        checkClass: 'checkbox accent-drdpblue-300 cursor-pointer',
      },
      { columnDef: 'lastName', header: t('lastName'), type: 'text' },
      { columnDef: 'firstName', header: t('firstName'), type: 'text' },
    ];

    if (this.permissionService.checkPermission(Permission.ViewDualEnrollments)) {
      this.tableColumns.push(
        {
          columnDef: 'enrollment',
          header: t('enrollment'),
          type: 'text'
        }
      );
    }
    this.tableColumns.push(
      { columnDef: 'dob', header: t('dob'), type: 'date' },
      { columnDef: 'siteName', header: t('site'), type: 'text' },
      { columnDef: 'className', header: t('class'), type: 'text' },
      { columnDef: 'teacherOfRecordName', header: t('teacher'), type: 'text' },
      {
        columnDef: 'enrollmentStartDate',
        header: t('agencyEnrollment'),
        type: 'date',
      },
      {
        columnDef: 'enrollmentEndDate',
        header: t('agencyWithdrawal'),
        type: 'date',
      },
      {
        columnDef: 'ageGradeInstrumentName',
        header: t('ageInstrument'),
        type: 'text',
      },
      { columnDef: 'View Student', header: t('viewStudent'), type: 'view' },
    );
  }

  ngAfterViewInit() {
    this.dataSource.paginator = this.paginator;
    this.paginator._intl.itemsPerPageLabel = '';
  }

  initializeTableData(res?: any): void {
    res.items = res.items.map((data: any) => ({
      ...data,
      displayIcons: setIconDisplayObject(this.tableColumns, data)
    }));
    this.dataSource = new MatTableDataSource(res.items);
    this.totalData = res.totalData;
    this.tableData = res.items;
  }

  initializeForm(): void {
    this.childSearch = this.fb.group({
      firstName: [null, [Validators.maxLength(100)]],
      lastName: [null, [Validators.maxLength(100)]],
      stateId: [null],
      agencyId: [null, [Validators.required]],
      ageGradeInstrumentId: [null],
      SSID: [null, [Validators.maxLength(100)]],
      localId: [null, [Validators.maxLength(100)]],
      drdpId: [null, [Validators.maxLength(100)]],
      DOB: [null],
      ratingPeriodId: [null],
      classroomIds: [null],
      siteIds: [null, [Validators.required]],
    });

    const { stateId, agencyId, sites } = this.user;
    if (stateId) this.childSearch.patchValue({ stateId: stateId });
    if (agencyId) this.childSearch.patchValue({ agencyId: agencyId });
    if (sites?.length) {
      const siteIds = sites.map((s: any) => s.siteId);
      this.siteIds.value = siteIds;
      this.childSearch.patchValue({ siteIds: siteIds });
    }
  }

  getSsidLabel(stateId: number): void {
    this.stateService
      .getStateConfigurations(stateId)
      .pipe(
        take(1),
        tap((config: IStateConfiguration[]) => {
          const ssid = config.find(
            (x: any) => x.columnName === StudentDemographic.SSID
          );
          this.ssidLabel = ssid?.label ? ssid?.label : this.ssidLabel;
          this.ssidPlaceholder = ssid?.label ? ssid?.label : this.ssidLabel;
        })
      )
      .subscribe();
  }

  handleDropdown(event: any, type: string): void {
    switch (type) {
      case SelectType.States:
        this.stateId.setValue(event?.id);
        this.agencyId.setValue(null);
        this.siteIds.setValue(null);
        this.ratingPeriodId.setValue(null);
        this.classroomIds.setValue(null);
        break;
      case SelectType.Agencies:
        this.agencyId.setValue(event?.id);
        this.ratingPeriodId.setValue(null);
        if (this.initialAgencySetupComplete && this.initialSiteSetupComplete && this.agencyId.value !== event?.id)
          this.siteIds.setValue(null);
        this.classroomIds.setValue(null);
        this.initialAgencySetupComplete = true;
        this.isSoftImplementation.agency = event?.isSoftImplementation || false;
        break;
      case SelectType.Sites:
        const siteIds = event?.map((sites: any) => sites.id);
        this.siteIds.setValue(siteIds?.length ? siteIds :null);
        this.classroomIds.setValue(null);
        this.initialSiteSetupComplete = true;
        this.isSoftImplementation.site = event?.some((site: ISites) => site.isSoftImplementation) || false;
        break;
      case SelectType.RatingPeriods:
        this.ratingPeriodId.setValue(event?.id);
        this.selectedSchoolYearId = event?.schoolYearId ?? this.currentSchoolYear;
        break;
      case SelectType.Class:
        const classroomIds = event?.map((classroom: any) => classroom.id);
        this.classroomIds.setValue(classroomIds.length ? classroomIds : null);
        this.isSoftImplementation.classroom = event?.some((classroom: IClassroom) => classroom.isSoftImplementation) || false;
        break;
      case SelectType.AgeInstrument:
        this.ageGradeInstrumentId.setValue(event?.id);
        break;
    }
  }

  handleDate(event: any): void {
    this.DOB.setValue(event);
  }

  handleCheckbox(event: any): void {
    if (!this.isSuperUser && event.data.enrollmentEndDate) {
      event.event.target.checked = false;
      this.toastService.error(
        this.translocoService.translate('user.manageEnrollment.searchManageChild.noLongerEnrolled')
      );
      return;
    }
    const { data } = event;
    data.isSelected = !data.isSelected;

    const index = this.studentBatch.findIndex(
      (student) => (student.id === data.id && student.classId === data.classId && student.enrollment == data.enrollment)
    );

    if (data.isSelected) {
      if (index === -1) {
        this.studentBatch.push(data);
      }
    } else {
      if (index !== -1) {
        this.studentBatch.splice(index, 1);
        if (this.selectAllPagesSet.has(this.paginator.pageIndex)) {
          this.isSelectAll = false;
          this.selectAllPagesSet.delete(this.paginator.pageIndex);
        }
      }
    }
  }

  search(): void {
    this.paginateTable(true);
  }

  onClear(): void {
    this.initialAgencySetupComplete = false;
    this.initialSiteSetupComplete = false;
    this.clearDropdowns = true;
    this.selectedSchoolYearId = null;

    this.tableData = [];
    this.dataSource = new MatTableDataSource<any>([]);
    this.totalData = 0;


    const accessToAll = this.permissionService.checkPermission(
      Permission.AccessAllAgencies
    );

    if (accessToAll) {
      this.childSearch.reset();
    } else {
        this.childSearch.reset({
        stateId: this.stateId.value,
        agencyId: this.agencyId.value,
      });
    }
    setTimeout(() => (this.clearDropdowns = false));
  }

  paginateTable(resetPage: boolean): void {
    if (this.childSearch.invalid) {
      this.toastService.error(
        this.translocoService.translate('user.manageEnrollment.common.requiredFieldsError')
      );
      return;
    }

    if (resetPage) this.studentBatch.length = 0;

    const currentAgencyId = this.agencyId.value;
    const currentRatingPeriodId = this.ratingPeriodId.value;

    if (
      currentAgencyId !== this.previousAgencyId ||
      currentRatingPeriodId !== this.previousRatingPeriodId
    ) {
      this.studentBatch = [];
      this.isSelectAll = false;
    }

    this.previousAgencyId = currentAgencyId;
    this.previousRatingPeriodId = currentRatingPeriodId;

    const siteIds = this.siteIds.value?.length
      ? this.siteIds.value.toString()
      : null;
    const pageIndex = resetPage ? 0 : this.paginator.pageIndex + 1;
    const itemsPerPage = resetPage ? 10 : this.paginator.pageSize;

    const payload: IStudentSearchRequestDto = {
      ...this.childSearch.value,
      siteIds,
      pageIndex,
      itemsPerPage,
    };

    this.studentService
      .search(payload)
      .pipe(
        take(1),
        tap((response: any) => {
          if (resetPage) {
            this.paginator.firstPage();
          }
          const { children } = response;
          const selectedBatchIds = this.studentBatch.map((batch) => batch.id);

          children.items.forEach((student: IStudentSearchResponseDto) => {
            student.isSelected = selectedBatchIds.includes(student.id);
          });

          this.initializeTableData(children);

          if (children.totalData > 25) {
            this.pageSizeOptions.push(response.totalData);
          }

          this.isSelectAll = false;
        })
      )
      .subscribe();
  }

  selectAllStudents(): void {
    if (this.selectAllPagesSet.has(this.paginator.pageIndex)) {
      this.isSelectAll = false;
      this.selectAllPagesSet.delete(this.paginator.pageIndex);
    } else {
      this.isSelectAll = true;
    }

    if (this.tableData) {
      this.tableData.forEach((student: any) => {
        student.isSelected = this.isSelectAll;
      });

      if (this.isSelectAll) {
        this.tableData.forEach((student: any) => {
          const index = this.studentBatch.findIndex(
            (s: any) => s.id === student.id
          );
          if (index === -1) {
            this.studentBatch.push(student);
          }
          this.selectAllPagesSet.add(this.paginator.pageIndex);
        });
      } else {
        this.studentBatch = this.studentBatch.filter((student: any) =>
          !this.tableData.some((tb: any) => tb.id == student.id)
        );
      }
    }
  }

  private handleActiveCheckAndOpenModal(modalComponent: any, modalData: any, displayConfirmationModal: boolean = true): Observable<any> {
    const enrollmentIds = this.studentBatch
      .map(student => student.sceId)
      .filter((id): id is number => id !== undefined && id !== null);

    const openModal = () => {
      const modalRef = this.modal.open(modalComponent, { data: modalData });
      return modalRef.afterClosed();
    }

    let confirmationModal$ = displayConfirmationModal ? this.ratingService.assessmentConfirmationModal() : of(true);
    return confirmationModal$.pipe(
      switchMap((confirmed: boolean) => {
        if (!confirmed) return of(false);

        return this.enrollmentService.areEnrollmentsActive({ enrollmentIds: enrollmentIds }).pipe(
          take(1),
          switchMap((isActive: boolean) => {
            if (isActive) return openModal();
            this.toastService.error(
              this.translocoService.translate('user.manageEnrollment.searchManageChild.alreadyMoved')
            );
              return of(false);
          })
        );
      })
    );
  }

  batchTransfer(): void {
    if (this.studentBatch.length < 1) {
      this.toastService.error(
        this.translocoService.translate('user.manageEnrollment.searchManageChild.noStudentsToTransfer')
      );
      return;
    }

    const batchData = {
      studentBatch: this.studentBatch,
      agencyId: this.agencyId.value,
    };

    this.handleActiveCheckAndOpenModal(TransferChildComponent, batchData).subscribe((response) => {
      if (response.success) {
        this.paginateTable(true);
      } else if (response.duplicates) {
        this.showDuplicateModal(response.duplicates, 'transfer');
      }
    });
  }

  batchReEnroll(): void {
    if (this.studentBatch.length < 1) {
      this.toastService.error(
        this.translocoService.translate('user.manageEnrollment.searchManageChild.noStudentsToReEnroll')
      );
      return;
    }
    const batchClassroom = [
      ...new Set(this.studentBatch.map((student) => student.classId)),
    ];

    if (batchClassroom.length > 1) {
      let event = { data: { modalInfo: {} } };
      event.data.modalInfo = {
        title: '',
        message: this.translocoService.translate(
          'user.manageEnrollment.searchManageChild.mixedClassroomsError'
        ),
        name: '',
        primaryBtnClass: 'blue',
        primaryBtnText: Modal.OK,
      };
      const modalRef = this.modal.open(ConfirmationModalComponent, {
        data: event ? event : undefined,
      });
      modalRef.afterClosed().subscribe();
      return;
    }

    const isSoftImplementation = this.isSoftImplementation.classroom || this.isSoftImplementation.site || this.isSoftImplementation.agency;

    const batchData = {
      students: this.studentBatch,
      name: this.studentBatch[0].className,
      classroomId: this.studentBatch[0].classroomId,
      fromClassId: batchClassroom[0],
      ratingPeriodId: this.studentBatch[0].ratingPeriodId,
      agencyId: this.agencyId.value,
      stateId: this.stateId.value,
      isSoftImplementation: isSoftImplementation
    };

    this.handleActiveCheckAndOpenModal(BatchReEnrollChildComponent, batchData).subscribe((response) => {
      if (response.success) {
        this.paginateTable(true);
      } else if (response.duplicates) {
        this.showDuplicateModal(response.duplicates, 're-enroll');
      }
    });
  }

  batchWithdraw(): void {
    if (this.studentBatch.length < 1) {
      this.toastService.error(
        this.translocoService.translate('user.manageEnrollment.searchManageChild.noStudentsToWithdraw')
      );
      return;
    }

    const batchData = {
      students: this.studentBatch,
      isBatch: true,
    };

    this.handleActiveCheckAndOpenModal(DropChildComponent, batchData, false).subscribe((response) => {
      if (response.success) {
        this.paginateTable(true);
      } else if (response.duplicates) {
        this.showDuplicateModal(response.duplicates, 'withdraw');
      }
    });
  }

  showDuplicateModal(students: string[], type: string) {
    let modal = { data: { modalInfo: {} } };

    let studentList = '<ul>';

    students.forEach((s) => {
      studentList += `<li> - ${s}</li>`;
    });
    studentList += '</ul>';
    const messageKey =
      type === 'transfer'
        ? 'user.manageEnrollment.searchManageChild.duplicateTransferRequests'
        : type === 're-enroll'
          ? 'user.manageEnrollment.searchManageChild.duplicateReEnrollRequests'
          : 'user.manageEnrollment.searchManageChild.duplicateWithdrawRequests';

    modal.data.modalInfo = {
      title: this.translocoService.translate('user.manageEnrollment.searchManageChild.errorTitle'),
      message: this.translocoService.translate(messageKey, { studentList }),
      name: 'Error',
      primaryBtnText: Modal.OK,
    };

    const modalRef = this.modal.open(ConfirmationModalComponent, {
      data: modal,
    });

    modalRef.afterClosed().subscribe((res) => {
      if (res) {
        modalRef.close({ success: false });
      }
    });
  }

  openViewChild(viewData: IModalEmitData): void {
    if (!this.isSuperUser && viewData.data.enrollmentEndDate) {
      this.toastService.error(
        this.translocoService.translate('user.manageEnrollment.searchManageChild.noLongerEnrolled')
      );
      return;
    }
    this.router.navigate(['/view-child/' + viewData.data.id]);
  }
}
